import type {
  DeviceDuplicateCandidate,
  DirectoryMatchStrength,
  DuplicateShortlist,
  VendorDuplicateCandidate,
} from '@mvcp/contracts';
import { deviceLabel } from './device-label';
import { STATUS_LABEL } from './statusPill';

/**
 * The duplicate shortlist beside a proposal (Story 8.12) — the SINGLE web-local owner of the
 * sentence a human reads for a match strength.
 *
 * **The split, and why there is no source-scan guard here.** `strength` is a DOMAIN verdict
 * (`packages/domain/src/directory-dedup.ts`); the wording is web-local, in this one component
 * — the same split AD-21 records for the band vocabulary and AD-25 for the device label. But
 * unlike those two there is no second copy to prevent: exactly one file renders this
 * vocabulary, and a source scan whose corpus is one file proves nothing while reading like
 * coverage. So the choice is LOGGED rather than dressed up as a guard. The day a second
 * surface renders a strength, that is the day the guard becomes worth writing.
 *
 * **An empty list never claims uniqueness.** The matcher compares names and models against
 * the curated catalog with a deliberately narrow rule and no company-suffix or brand-alias
 * list, so "nothing matched" is exactly what an empty list means — see {@link emptyNotice}.
 *
 * **The cap is disclosed, not silent.** When the server found more than it sent, the surplus
 * is stated and the full catalog stays reachable through the row's own "Merge into…" picker,
 * which this component deliberately does NOT narrow: replacing the whole catalog with a
 * five-row shortlist would make a legitimate non-obvious merge unreachable.
 */

/** The strength vocabulary — the one place these words exist. */
const STRENGTH_LABEL: Record<DirectoryMatchStrength, string> = {
  exact: 'Exact match',
  strong: 'Likely the same',
  weak: 'Possible match',
};

/** The tone each strength renders with, mapped from the same one place. */
const STRENGTH_TONE: Record<DirectoryMatchStrength, string> = {
  exact: 'bad',
  strong: 'warn',
  weak: 'neutral',
};

/**
 * What an EMPTY shortlist says. Deliberately not "no duplicates" and not "this record is
 * new": the check is a name/model comparison against the curated catalog, and an
 * abbreviation, a translated name or a renamed product line passes it untouched.
 *
 * Named per QUEUE (review 2026-08-03): one string said "matched this name" on both, but the
 * value compared on the device queue is the MODEL. Telling an administrator that nothing
 * matched a name, on a surface whose whole subject is a model, misdescribes the check the
 * sentence exists to be honest about.
 */
function emptyNotice(kind: 'vendor' | 'device'): string {
  const compared = kind === 'device' ? 'model' : 'name';
  return `Nothing in the curated catalog matched this ${compared} — which is not a check that it is new.`;
}

/** How the surplus above the cap is stated, so the shortlist cannot read as exhaustive. */
function remainderNotice(shown: number, totalFound: number): string | null {
  const more = totalFound - shown;
  if (more <= 0) return null;
  return `${more} more possible ${more === 1 ? 'match' : 'matches'} not shown — use “Merge into…” to reach the whole catalog.`;
}

/**
 * `2026-07-31T…` → `2026-07-31` in the READER's timezone, the granularity that distinguishes
 * two catalog rows.
 *
 * Not `iso.slice(0, 10)` (review 2026-08-03): that is the UTC date, so for any administrator
 * west of UTC a row created after 16:00 local read as the following day, and two rows created
 * minutes apart across midnight UTC read a day apart — on a fact the contract calls
 * decision-grade precisely because "which one the catalog has held longer" is often the only
 * thing telling two identical-reading rows apart. `en-CA` is the terse route to `YYYY-MM-DD`.
 */
function onDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso.slice(0, 10) : d.toLocaleDateString('en-CA');
}

const factStyle: React.CSSProperties = { fontSize: 12 };

/** One `label: value` fact, omitted entirely when there is no value to show. */
function Fact({ label, value }: { label: string; value: string | null }): React.JSX.Element | null {
  if (value === null) return null;
  return (
    <span className="muted" style={factStyle}>
      {label} <span style={{ color: 'var(--text)' }}>{value}</span>
    </span>
  );
}

/**
 * The shortlist for ONE proposal row.
 *
 * `onMergeInto` hands the caller a curated row's id; the caller runs its EXISTING merge with
 * that target, so Story 8.5's confirm-before-destroying-a-revision modal and Story 8.10's
 * per-row failure slot both apply unchanged — this component owns no request and no error
 * surface of its own.
 *
 * `describedBy` is the row's `RowErrorSlot` id, so a merge started from a candidate can say
 * why it failed on the control that started it.
 */
export function DuplicateCandidates(
  props: {
    /** The proposal's own id — only used to build stable, unique element ids. */
    rowId: string;
    /** How the proposal reads, for the merge control's accessible name. */
    subject: string;
    submitting: boolean;
    describedBy: string;
    onMergeInto: (candidateId: string) => void;
  } & (
    | { kind: 'vendor'; shortlist: DuplicateShortlist<VendorDuplicateCandidate> }
    | { kind: 'device'; shortlist: DuplicateShortlist<DeviceDuplicateCandidate> }
  ),
): React.JSX.Element {
  const { rowId, subject, submitting, describedBy, onMergeInto } = props;
  const headingId = `inbox-candidates-${rowId}`;

  /**
   * Both candidate kinds normalised to ONE render shape, chosen inside the discriminated
   * branch so neither half needs a cast and neither can read the other's fields. The markup
   * below is then a single path — two nearly-identical JSX blocks are how two lists of the
   * same thing drift apart while both stay green.
   *
   * A device's own name is composed through the ONE web label owner (AD-25): two revisions of
   * one model are an `exact` candidate BY DESIGN, so a bare-model name would render them as
   * two identical rows on the one surface built to tell them apart.
   */
  const rows =
    props.kind === 'device'
      ? props.shortlist.candidates.map((c) => ({
          id: c.id,
          strength: c.strength,
          createdAt: c.createdAt,
          named: deviceLabel({ model: c.model, version: c.version }),
          facts: [
            { label: 'by', value: c.vendorName },
            { label: 'category', value: c.category ?? null },
            { label: 'installs', value: String(c.installCount) },
            { label: 'certificates', value: String(c.certificateCount) },
          ],
        }))
      : props.shortlist.candidates.map((c) => ({
          id: c.id,
          strength: c.strength,
          createdAt: c.createdAt,
          named: c.name,
          facts: [
            { label: 'country', value: c.country ?? null },
            // Through the EXISTING label owner, not the raw wire enum (review 2026-08-03).
            // This rendered a bare lowercase `unverified` while `statusPill.tsx` has owned
            // `unverified → 'Unverified'` all along — the exact drift AD-21 exists to prevent,
            // introduced by the one file whose own header calls itself the single web-local
            // owner of the sentence a human reads.
            { label: 'status', value: STATUS_LABEL[c.verificationStatus] },
            { label: 'devices', value: String(c.deviceCount) },
          ],
        }));
  const remainder = remainderNotice(rows.length, props.shortlist.totalFound);

  return (
    // `role="group"`, not a bare div: `aria-labelledby` on a roleless element names nothing,
    // so the shortlist had no accessible identity at all — a screen-reader user met a run of
    // facts and destructive buttons with no boundary telling them what the run was. `group` is
    // the generic named container and needs no landmark semantics on a row-level block.
    /*
      The group's ACCESSIBLE NAME carries the row and the count (review 2026-08-03); the
      visible caption below stays generic. It used to be named "Possible duplicates in the
      catalog" on every proposal, so an inbox of thirty clean rows gave a screen-reader user
      thirty identically-named groups with no way to tell which proposal any belonged to —
      while the merge button inside has always interpolated `subject`. The count is in the
      name too, so navigating BY GROUP says whether there is anything inside before entering.

      `aria-label` rather than extending the visible heading: `aria-labelledby` would win over
      it, the subject already reads at full size two lines above, and repeating it in visible
      12px muted text is redundancy for sighted users to pay for a benefit only assistive
      technology collects. A group container is not an interactive control, so WCAG 2.5.3
      Label in Name — the rule that governs the curation inputs — does not apply here.
    */
    <div
      className="stack"
      style={{ gap: 6 }}
      role="group"
      aria-label={
        rows.length === 0
          ? `Possible duplicates for ${subject}: none found`
          : `Possible duplicates for ${subject}: ${rows.length}`
      }
    >
      <span id={headingId} className="muted" style={{ fontSize: 12, fontWeight: 700 }}>
        Possible duplicates in the catalog
      </span>

      {rows.length === 0 ? (
        <span className="muted" style={factStyle}>
          {emptyNotice(props.kind)}
        </span>
      ) : (
        <ul className="stack" style={{ listStyle: 'none', margin: 0, padding: 0, gap: 6 }}>
          {rows.map((c) => {
            const named = c.named;
            return (
              <li
                key={c.id}
                className="stack"
                style={{
                  gap: 4,
                  padding: '6px 8px',
                  background: 'var(--surface-2)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--rounded-md)',
                }}
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                  <span className="status-pill" data-tone={STRENGTH_TONE[c.strength]}>
                    <span className="status-pill__dot" aria-hidden="true" />
                    {STRENGTH_LABEL[c.strength]}
                  </span>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>{named}</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                  {c.facts.map((f) => (
                    <Fact key={f.label} label={f.label} value={f.value} />
                  ))}
                  <Fact label="in the catalog since" value={onDate(c.createdAt)} />
                  <button
                    type="button"
                    className="icon-button"
                    disabled={submitting}
                    aria-describedby={describedBy}
                    // Named with BOTH rows: this button DELETES the proposal, and a page can
                    // carry several candidates and several proposals at once, so "Merge into
                    // this" alone would give a screen-reader user a list of identically named
                    // destructive controls.
                    aria-label={`Merge ${subject} into ${named}`}
                    onClick={() => onMergeInto(c.id)}
                  >
                    Merge into this
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {remainder && (
        <span className="muted" style={factStyle}>
          {remainder}
        </span>
      )}
    </div>
  );
}
